import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type {
  Bedrijf,
  BedrijfFilter,
  BedrijfInvoer,
  Contactpersoon,
  ContactpersoonInvoer,
  Paginering,
} from './typen.ts';

export const bedrijfSleutels = {
  alle: ['bedrijven'] as const,
  lijst: (filter: BedrijfFilter) => ['bedrijven', 'lijst', filter] as const,
  details: (id: string) => ['bedrijven', id] as const,
  contactpersonen: (id: string) => ['bedrijven', id, 'contactpersonen'] as const,
};

export const contactpersoonSleutels = {
  alle: ['contactpersonen'] as const,
  lijst: (zoek: string, paginering: Paginering) => ['contactpersonen', 'lijst', zoek, paginering] as const,
  overzicht: ['contactpersonen', 'overzicht'] as const,
};

/**
 * Zoekt bedrijven op naam.
 * @param filter De zoekterm, relatie en paginering.
 * @returns De query met de gevonden bedrijven.
 */
export function useBedrijven(filter: BedrijfFilter) {
  return useQuery({
    queryKey: bedrijfSleutels.lijst(filter),
    queryFn: () => uitvoeren(api.GET('/api/v1/bedrijven', { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Zoekt bedrijven voor een keuzelijst.
 * @param zoek De zoekterm.
 * @returns De gevonden bedrijven.
 */
export function zoekBedrijven(zoek: string): Promise<Bedrijf[]> {
  return uitvoeren(api.GET('/api/v1/bedrijven', { params: { query: zoek ? { zoek, limiet: 25 } : { limiet: 25 } } }));
}

/**
 * Haalt een bedrijf op met locatie en contactpersonen.
 * @param id Het id van het bedrijf.
 * @returns De query met het bedrijf.
 */
export function useBedrijf(id: string | null | undefined) {
  return useQuery({
    queryKey: bedrijfSleutels.details(id ?? ''),
    queryFn: () => uitvoeren(api.GET('/api/v1/bedrijven/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

/**
 * Somt de contactpersonen van een bedrijf op.
 * @param bedrijfId Het id van het bedrijf.
 * @returns De query met de contactpersonen.
 */
export function useContactpersonenVanBedrijf(bedrijfId: string | null | undefined) {
  return useQuery({
    queryKey: bedrijfSleutels.contactpersonen(bedrijfId ?? ''),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/bedrijven/{id}/contactpersonen', {
          params: { path: { id: bedrijfId ?? '' }, query: { limiet: 500 } },
        }),
      ),
    enabled: Boolean(bedrijfId),
  });
}

/**
 * Zoekt contactpersonen op naam of e-mailadres.
 * @param zoek De zoekterm.
 * @param paginering Limiet en offset.
 * @returns De query met de gevonden contactpersonen.
 */
export function useContactpersonen(zoek: string, paginering: Paginering) {
  return useQuery({
    queryKey: contactpersoonSleutels.lijst(zoek, paginering),
    queryFn: () =>
      uitvoeren(api.GET('/api/v1/contactpersonen', { params: { query: zoek ? { zoek, ...paginering } : paginering } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Alle contactpersonen op id, om namen te tonen bij gesprekken en vacatures. De API heeft geen route om één
 * contactpersoon op te halen, dus de lijst wordt in één keer opgehaald en een minuut bewaard.
 * @returns De query met een map van id naar contactpersoon.
 */
export function useContactpersonenOverzicht() {
  return useQuery({
    queryKey: contactpersoonSleutels.overzicht,
    queryFn: async () => {
      const lijst = await uitvoeren(api.GET('/api/v1/contactpersonen', { params: { query: { limiet: 500 } } }));
      return new Map(lijst.map((contactpersoon) => [contactpersoon.id, contactpersoon]));
    },
    staleTime: 60_000,
  });
}

/**
 * Zoekt contactpersonen voor een keuzelijst.
 * @param zoek De zoekterm.
 * @returns De gevonden contactpersonen.
 */
export function zoekContactpersonen(zoek: string): Promise<Contactpersoon[]> {
  return uitvoeren(
    api.GET('/api/v1/contactpersonen', { params: { query: zoek ? { zoek, limiet: 25 } : { limiet: 25 } } }),
  );
}

/**
 * De mutaties op bedrijven.
 * @returns Maken, wijzigen en verwijderen.
 */
export function useBedrijfMutaties() {
  return {
    maak: useApiMutatie(
      (invoer: BedrijfInvoer) => uitvoeren(api.POST('/api/v1/bedrijven', { body: invoer })),
      () => [bedrijfSleutels.alle],
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: Partial<BedrijfInvoer> }) =>
        uitvoeren(api.PATCH('/api/v1/bedrijven/{id}', { params: { path: { id } }, body: invoer })),
      () => [bedrijfSleutels.alle, ['vacatures']],
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/bedrijven/{id}', { params: { path: { id } } })),
      () => [bedrijfSleutels.alle, contactpersoonSleutels.alle],
    ),
  };
}

/**
 * De mutaties op contactpersonen.
 * @returns Toevoegen, wijzigen en verwijderen.
 */
export function useContactpersoonMutaties() {
  const verversen = () => [bedrijfSleutels.alle, contactpersoonSleutels.alle, ['vacatures']];
  return {
    voegToe: useApiMutatie(
      ({ bedrijfId, invoer }: { bedrijfId: string; invoer: ContactpersoonInvoer }) =>
        uitvoeren(
          api.POST('/api/v1/bedrijven/{id}/contactpersonen', { params: { path: { id: bedrijfId } }, body: invoer }),
        ),
      verversen,
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: Partial<ContactpersoonInvoer> }) =>
        uitvoeren(api.PATCH('/api/v1/contactpersonen/{id}', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/contactpersonen/{id}', { params: { path: { id } } })),
      verversen,
    ),
  };
}
